// CSV Export Utility for Query Results

// Escape special characters for CSV format
const escapeCsvValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // If value contains comma, quote, or newline, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

// Convert array of objects to CSV string
const convertToCSV = (data) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Extract headers from first row
  const headers = Object.keys(data[0]);

  // Create header row
  const headerRow = headers.map(header => escapeCsvValue(header)).join(',');

  // Create data rows
  const dataRows = data.map(row => {
    return headers.map(header => escapeCsvValue(row[header])).join(',');
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
};

// Generate filename with timestamp
const generateFilename = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `query_results_${year}-${month}-${day}_${hours}${minutes}${seconds}.csv`;
};

// Export data to CSV file
export const exportToCSV = (data) => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  try {
    // Convert data to CSV format
    const csvContent = convertToCSV(data);

    // Create blob
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Generate filename
    const filename = generateFilename();

    // Create download link and trigger download
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // Modern browsers
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Fallback for older browsers
      window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
    }

    return filename;
  } catch (error) {
    console.error('Error exporting CSV:', error);
    throw new Error('Failed to export CSV file');
  }
};

// Validate data before export
export const validateExportData = (data) => {
  if (!data) {
    return { valid: false, error: 'No data available to export' };
  }

  if (!Array.isArray(data)) {
    return { valid: false, error: 'Invalid data format' };
  }

  if (data.length === 0) {
    return { valid: false, error: 'No results to export' };
  }

  return { valid: true, error: null };
};
